import { defineTheme } from '@astryxdesign/core/theme';
import { HEX_COLOR, onColor } from '@shared/color';

export const DEFAULT_ACCENT = '#4690FF';

/**
 * Runtime Astryx theme for one studio (`astryx docs theme` → Runtime vs
 * Built: runtime themes are the documented path for user branding in a
 * client-only app). The accent is the only per-studio colour and comes from
 * the database. The HCT scale derives the accent family (muted, hover, text
 * tones); the brand hex itself is pinned so the button shows the exact colour
 * the studio configured, and since that overrides one side of the pair we
 * own its label colour: `onColor` picks the WCAG-better of white/black.
 */
export function createStudioTheme(accent: string) {
  const seed = HEX_COLOR.test(accent) ? accent.toUpperCase() : DEFAULT_ACCENT;
  return defineTheme({
    name: `studio-${seed.slice(1).toLowerCase()}`,
    color: { accent: seed, neutralStyle: 'neutral', contrast: 'standard' },
    typography: {
      scale: { base: 16, ratio: 1.2 },
      body: {
        family: 'Inter Variable',
        fallbacks: '-apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
      },
      heading: { weight: 'semibold', weights: { 1: 'bold', 2: 'bold' } },
    },
    radius: { base: 4, multiplier: 1.5 },
    motion: { fast: 150, medium: 280, slow: 600, ratio: 0.75 },
    tokens: {
      '--color-accent': seed,
      '--color-on-accent': onColor(seed),
      // Brief: black background, white text. The app always runs in dark
      // mode; light values exist only so a forced light mode stays readable.
      '--color-background-body': ['#F4F5F7', '#000000'],
      '--color-background-surface': ['#FFFFFF', '#0E0F11'],
      '--color-background-card': ['#FFFFFF', '#121316'],
      '--color-background-popover': ['#FFFFFF', '#15161A'],
      '--color-text-primary': ['#0B0C0E', '#FFFFFF'],
      '--focus-outline-color': 'var(--color-accent)',
    },
  });
}
